import { useI18n } from '../../contexts/I18nContext';
import { useCycleTimeReport } from '../../hooks/useTaskReports';
import type { ReportFilters } from '../../lib/taskReportsApi';
import { ReportCard, SectionState } from './common';

// Cycle time (started → completed) and lead time (created → completed) for the
// tasks completed in the selected period, plus a per-project breakdown.

export function CycleTimeSection({ filters }: { filters: ReportFilters }) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useCycleTimeReport(filters);

  const days = (n: number | null) =>
    n === null ? '—' : t('reports.cycle.days', { value: String(Math.round(n * 10) / 10) });

  const kpis: Array<{ label: string; value: number | null }> = data
    ? [
        { label: t('reports.cycle.avgCycle'), value: data.avgCycleDays },
        { label: t('reports.cycle.medianCycle'), value: data.medianCycleDays },
        { label: t('reports.cycle.avgLead'), value: data.avgLeadDays },
        { label: t('reports.cycle.medianLead'), value: data.medianLeadDays },
      ]
    : [];

  return (
    <ReportCard title={t('reports.cycle.title')}>
      <SectionState isLoading={isLoading} isError={isError} />
      {data && data.count === 0 && (
        <p className="text-sm text-gray-400">{t('reports.cycle.empty')}</p>
      )}
      {data && data.count > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="rounded-md bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-500">{kpi.label}</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">{days(kpi.value)}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            {t('reports.cycle.count')}:{' '}
            <span className="font-medium text-gray-700">{data.count}</span>
          </p>
          {data.byProject.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">
                {t('reports.cycle.byProject')}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs">
                      <th className="px-3 py-2 text-left font-medium text-gray-500">
                        {t('reports.hours.project')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">
                        {t('reports.cycle.count')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">
                        {t('reports.cycle.avgCycle')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">
                        {t('reports.cycle.medianCycle')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">
                        {t('reports.cycle.avgLead')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byProject.map((p) => (
                      <tr key={p.projectId} className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-900">{p.name}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                          {p.count}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                          {days(p.avgCycleDays)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                          {days(p.medianCycleDays)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                          {days(p.avgLeadDays)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </ReportCard>
  );
}
